import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Input component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Border Radius: 6px (rounded-md)
 * - Heights: sm=32px (h-8), md=36px (h-9)
 * - Shadow: Multi-layer input shadow with focus ring
 * - Transitions: 150ms
 * - Placeholder: grey-400
 * - Error: Red shadow ring
 */
const inputVariants = cva(
  // Base styles with Hyperkit shadow and transitions
  [
    'flex w-full rounded-md bg-white text-sm text-grey-900',
    'px-3 transition-shadow duration-150 ease-out',
    'shadow-input',
    'placeholder:text-grey-400',
    'focus:outline-none focus:shadow-input-focus',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-50',
    // Dark mode
    'dark:bg-grey-900 dark:text-grey-50 dark:placeholder:text-grey-500',
  ],
  {
    variants: {
      inputSize: {
        sm: 'h-8 py-1.5',    // 32px height
        default: 'h-9 py-2',  // 36px height
      },
      error: {
        true: 'shadow-input-error focus:shadow-input-error',
        false: '',
      },
    },
    defaultVariants: {
      inputSize: 'default',
      error: false,
    },
  }
);

const Input = React.forwardRef(
  ({ className, type, inputSize, error, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          inputVariants({ inputSize, error }),
          // File input specific styles
          'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-grey-700',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
